import { createApp } from 'vue'
import { createHead } from '@unhead/vue/client'
import { VueHeadMixin } from '@unhead/vue/legacy'
import VueGtag from 'vue-gtag'
import { Amplify } from 'aws-amplify'

import App from './App.vue'
import awsconfig from './aws-exports'
import store from './store'
import router from './router'
import vuetify from './plugins/vuetify'
import i18n from './i18n'
import cookiesCompat from './plugins/cookies-compat'
import safeHtml from './plugins/safe-html'

// Vuetify styles are compiled through src/styles/settings.scss (see vite.config.js
// `styles.configFile`) — do not import 'vuetify/styles' here or the SASS layer is bypassed.
import '@fontsource/inter/latin-400.css'
import '@fontsource/inter/latin-ext-400.css'
// 500 backs the button and overline roles (DS --text-button-weight/--text-overline-weight);
// without it the browser substitutes 400 and button labels lose their tracking weight.
import '@fontsource/inter/latin-500.css'
import '@fontsource/inter/latin-ext-500.css'
// 600 is the semibold ceiling: prices, .mob-semibold, and CMS <b>/<strong>
// (mapped to 600 in tokens.css). No role requests 700, so it does not ship.
import '@fontsource/inter/latin-600.css'
import '@fontsource/inter/latin-ext-600.css'
import '@mdi/font/css/materialdesignicons.css'
import './styles/tokens.css'

// Vite (unlike the old webpack build) does not bundle moment's locales, so
// moment.locale('cs') silently fell back to English (e.g. "Monday" instead of
// "pondělí"). Import the locales the app supports (en is the default). Use the
// dist/ path so the locale registers on the same moment ESM build the app imports.
import 'moment/dist/locale/cs'
import 'moment/dist/locale/sk'
import 'moment/dist/locale/pl'
import 'moment/dist/locale/de'
import 'moment/dist/locale/hu'

Amplify.configure(awsconfig)

const app = createApp(App)

// Vuex 4 must be installed before the router so navigation guards see the store.
app.use(store)
app.use(router)
app.use(vuetify)
app.use(i18n)
app.use(createHead())
app.mixin(VueHeadMixin)
app.use(cookiesCompat)
app.use(safeHtml)

if (import.meta.env.DEV) {
  // Build-mode marker for the E2E run guard: DEV-only behavior (contacts auto-fill, debug
  // alerts) exists only in a dev-mode bundle, and the guard reads this flag off the
  // running app (__tests__/e2e/helpers/dev-mode.js).
  window.__MOB_DEV__ = true
}

// GA is installed deferred (bootstrap: false); the measurement ids (central
// VITE_GA_CENTRAL_ID + per-tenant mobConfig.gaTrackingId) are supplied at
// bootstrap time in App.vue's enableGtag(). Production only.
if (import.meta.env.PROD) {
  app.use(
    VueGtag,
    {
      bootstrap: false,
      config: {
        params: {
          send_page_view: true,
        },
      },
    },
    router
  )
}

app.mount('#app')
